// v2 boat — a lapstrake rowing skiff: the hull is three stepped clinker strakes
// (each band flaring out a little wider than the one below, so the planking reads
// in the silhouette), with a tarred bottom band, a small bow deck under a raked
// stem, two rowing thwarts, a pair of oars shipped along the gunwales with their
// blades trailing aft, oarlock pins amidships and a stern skeg. Bow faces +z.
// Ported 1:1 from age-of-mcp's v2/boat.ts (the source uses no shared helpers —
// every primitive is inline).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const TAR    = "#5c4326";   // tarred garboard (bottom strake) & skeg
const PLANK  = "#9a7848";   // mid strake
const SHEER  = "#b29260";   // weathered top strake
const RAIL   = "#4a2e10";   // dark walnut gunwale rail
const FLOOR  = "#6b4820";   // floorboards
const DECK   = "#8a6f48";   // bow deck & thwarts
const OAR    = "#c2a36b";   // oar shafts
const BLADE  = "#d8c190";   // pale oar blades

// Oval scale that turns each circular band into the hull plan shape.
const SX = 0.55;   // beam squish
const SZ = 1.55;   // fore-aft stretch
const SEGS = 8;    // octagonal low-poly bands

function boatParts(): Part[] {
  const parts: Part[] = [];

  // Clinker strakes: three open cylinder bands stacked with a small overlap;
  // each band's bottom radius is at or outside the band below's top radius, so
  // the hull shows the stepped lapstrake profile instead of one smooth wall.
  // Garboard (tarred bottom band): 0 .. 0.085
  parts.push({
    geometry: new THREE.CylinderGeometry(0.24, 0.185, 0.085, SEGS, 1, true),
    color: TAR, position: [0, 0.0425, 0], scale: [SX, 1, SZ],
  });
  // Mid strake: 0.075 .. 0.155 (laps over the garboard)
  parts.push({
    geometry: new THREE.CylinderGeometry(0.285, 0.245, 0.08, SEGS, 1, true),
    color: PLANK, position: [0, 0.115, 0], scale: [SX, 1, SZ],
  });
  // Sheer strake: 0.145 .. 0.22
  parts.push({
    geometry: new THREE.CylinderGeometry(0.315, 0.29, 0.075, SEGS, 1, true),
    color: SHEER, position: [0, 0.1825, 0], scale: [SX, 1, SZ],
  });

  // Floorboards — a solid low disk inside the hull.
  parts.push({
    geometry: new THREE.CylinderGeometry(0.17, 0.17, 0.025, SEGS),
    color: FLOOR, position: [0, 0.0225, 0], scale: [SX, 1, SZ],
  });

  // Gunwale rail — an elliptical torus riding the sheer strake's rim.
  parts.push({
    geometry: new THREE.TorusGeometry(0.315, 0.016, 4, SEGS),
    color: RAIL, position: [0, 0.22, 0],
    rotation: [Math.PI / 2, 0, 0], scale: [SX, SZ, 1],
  });

  // Bow deck & raked stem (+z).
  parts.push({
    geometry: new THREE.BoxGeometry(0.18, 0.022, 0.14),
    color: DECK, position: [0, 0.215, 0.33],
  });
  parts.push({
    geometry: new THREE.BoxGeometry(0.032, 0.10, 0.032),
    color: RAIL, position: [0, 0.245, 0.46], rotation: [-0.35, 0, 0],
  });

  // Stern skeg (-z) — a thin raked fin under the stern run.
  parts.push({
    geometry: new THREE.BoxGeometry(0.022, 0.07, 0.14),
    color: TAR, position: [0, 0.035, -0.43], rotation: [0.25, 0, 0],
  });

  // Rowing thwarts.
  parts.push({
    geometry: new THREE.BoxGeometry(0.27, 0.024, 0.075),
    color: DECK, position: [0, 0.165, 0.10],
  });
  parts.push({
    geometry: new THREE.BoxGeometry(0.25, 0.024, 0.075),
    color: DECK, position: [0, 0.165, -0.16],
  });

  // Shipped oars + oarlock pins — one oar rests along each gunwale, shaft
  // forward, blade trailing aft.
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.011, 0.011, 0.52, 5),
      color: OAR, position: [sx * 0.165, 0.235, -0.02],
      rotation: [Math.PI / 2, 0, 0],
    });
    parts.push({
      geometry: new THREE.BoxGeometry(0.034, 0.012, 0.13),
      color: BLADE, position: [sx * 0.165, 0.235, -0.34],
    });
    parts.push({
      geometry: new THREE.BoxGeometry(0.014, 0.03, 0.014),
      color: RAIL, position: [sx * 0.168, 0.245, 0.06],
    });
  }

  return parts;
}

export const boatV2 = defineModel({
  id: "boat-v2",
  batchId: "fable",
  name: "Boat (v2)",
  tags: ["vehicle","boat","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly boat.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.42,
  parts: boatParts,
  animations: [],
});
